/**
 * Seeded 2D simplex noise plus the usual fractal combinators (fBm, ridged multifractal,
 * domain warping). Zero dependencies and pure arithmetic: the gradient table is written as
 * literals and nothing here calls Math.sin/cos/exp, so results are bit-identical on every
 * JavaScript engine (IEEE-754 +, *, floor and sqrt are exactly specified).
 */
import type { Rng } from '../core/rng';

// 16 unit gradients, 22.5° apart. Literal values instead of Math.cos/sin (see header).
const C1 = 0.9238795325112867;
const C2 = Math.SQRT1_2;
const C3 = 0.3826834323650898;
const GRAD_X = new Float64Array([1, C1, C2, C3, 0, -C3, -C2, -C1, -1, -C1, -C2, -C3, 0, C3, C2, C1]);
const GRAD_Y = new Float64Array([0, C3, C2, C1, 1, C1, C2, C3, 0, -C3, -C2, -C1, -1, -C1, -C2, -C3]);

const SQRT3 = Math.sqrt(3);
const F2 = 0.5 * (SQRT3 - 1);
const G2 = (3 - SQRT3) / 6;
/** Empirical scale that maps the raw kernel sum onto ≈[-1, 1] for unit-length gradients. */
const OUTPUT_SCALE = 99;

/** One independent noise field. Construct several from the same Rng for uncorrelated layers. */
export class Noise {
  /** Doubled permutation table with the gradient index pre-folded in (perm & 15). */
  private readonly perm: Uint8Array;
  private readonly grad: Uint8Array;
  /** Per-field offset so different fields never share the lattice origin. */
  private readonly ox: number;
  private readonly oy: number;

  constructor(rng: Rng) {
    const p = new Uint8Array(256);
    for (let i = 0; i < 256; i++) p[i] = i;
    for (let i = 255; i > 0; i--) {
      const j = rng.int(0, i);
      const t = p[i];
      p[i] = p[j];
      p[j] = t;
    }
    this.perm = new Uint8Array(512);
    this.grad = new Uint8Array(512);
    for (let i = 0; i < 512; i++) {
      this.perm[i] = p[i & 255];
      this.grad[i] = p[i & 255] & 15;
    }
    this.ox = rng.range(-1000, 1000);
    this.oy = rng.range(-1000, 1000);
  }

  /** Classic 2D simplex noise (Gustavson), ≈[-1, 1], feature size ≈ 1 unit. */
  simplex(xIn: number, yIn: number): number {
    const x = xIn + this.ox;
    const y = yIn + this.oy;
    const s = (x + y) * F2;
    const i = Math.floor(x + s);
    const j = Math.floor(y + s);
    const t = (i + j) * G2;
    const x0 = x - (i - t);
    const y0 = y - (j - t);
    const i1 = x0 > y0 ? 1 : 0;
    const j1 = 1 - i1;
    const x1 = x0 - i1 + G2;
    const y1 = y0 - j1 + G2;
    const x2 = x0 - 1 + 2 * G2;
    const y2 = y0 - 1 + 2 * G2;
    const ii = i & 255;
    const jj = j & 255;
    const perm = this.perm;
    const grad = this.grad;
    let n = 0;
    let t0 = 0.5 - x0 * x0 - y0 * y0;
    if (t0 > 0) {
      const g = grad[ii + perm[jj]];
      t0 *= t0;
      n += t0 * t0 * (GRAD_X[g] * x0 + GRAD_Y[g] * y0);
    }
    let t1 = 0.5 - x1 * x1 - y1 * y1;
    if (t1 > 0) {
      const g = grad[ii + i1 + perm[jj + j1]];
      t1 *= t1;
      n += t1 * t1 * (GRAD_X[g] * x1 + GRAD_Y[g] * y1);
    }
    let t2 = 0.5 - x2 * x2 - y2 * y2;
    if (t2 > 0) {
      const g = grad[ii + 1 + perm[jj + 1]];
      t2 *= t2;
      n += t2 * t2 * (GRAD_X[g] * x2 + GRAD_Y[g] * y2);
    }
    const v = OUTPUT_SCALE * n;
    return v > 1 ? 1 : v < -1 ? -1 : v;
  }

  /** Fractal Brownian motion: octaves of simplex noise, normalised to ≈[-1, 1]. */
  fbm(x: number, y: number, octaves: number, lacunarity = 2, gain = 0.5): number {
    let sum = 0;
    let amp = 1;
    let norm = 0;
    let f = 1;
    for (let o = 0; o < octaves; o++) {
      // Octave offsets decorrelate the octaves (they would otherwise all pass through 0 at the origin).
      sum += amp * this.simplex(x * f + o * 31.7, y * f - o * 17.3);
      norm += amp;
      amp *= gain;
      f *= lacunarity;
    }
    return sum / norm;
  }

  /**
   * Ridged multifractal in [0, 1]: sharp crests where the base noise crosses zero, which is what
   * turns a noise field into connected *chains* rather than round blobs. Each octave is weighted by
   * the previous one so detail concentrates on the ridges (Musgrave).
   */
  ridged(x: number, y: number, octaves: number, lacunarity = 2, gain = 0.5): number {
    let sum = 0;
    let amp = 1;
    let norm = 0;
    let f = 1;
    let weight = 1;
    for (let o = 0; o < octaves; o++) {
      let v = 1 - Math.abs(this.simplex(x * f + o * 31.7, y * f - o * 17.3));
      v *= v;
      v *= weight;
      weight = v * 2 > 1 ? 1 : v * 2;
      sum += v * amp;
      norm += amp;
      amp *= gain;
      f *= lacunarity;
    }
    return sum / norm;
  }
}

/**
 * Domain warp: displaces (x, y) by two independent fBm fields. Feeding warped coordinates into
 * another field produces the swirled, organic shapes (bays, curving ridges) plain fBm lacks.
 */
export function warp(
  nx: Noise,
  ny: Noise,
  x: number,
  y: number,
  frequency: number,
  amount: number,
  octaves = 3,
): [number, number] {
  return [
    x + amount * nx.fbm(x * frequency, y * frequency, octaves),
    y + amount * ny.fbm(x * frequency, y * frequency, octaves),
  ];
}
